import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Kunde, Grab, Vertrag, Tier } from '../types';
import kundeService from '../services/kundeService';
import grabService from '../services/grabService';
import vertragService from '../services/vertragService';
import tierService from '../services/tierService';
import {
  Button, Card, PageHeader, Select, LoadingScreen, EmptyState, Icon,
  inputClass, tableCard, tableScroll, th, td, tableRow,
  FilterStand, filterAktiv,
} from '../components/ui';
import KundeFormModal from '../components/KundeFormModal';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft } from '../utils/ankunft';

// Vertragsfilter: alle · hat überhaupt einen Vertrag · hat einen aktiven · hat gar keinen.
type VertragFilter = 'ALL' | 'MIT' | 'AKTIV' | 'OHNE';

const Kunden: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Kunde[]>([]);
  const [graeber, setGraeber] = useState<Grab[]>([]);
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [tiere, setTiere] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Kunde | null>(null);
  const [query, setQuery] = useState('');
  const [filterVertrag, setFilterVertrag] = useState<VertragFilter>('ALL');

  // Kunden sind nicht friedhofsgebunden – es werden immer alle angezeigt.
  const load = useCallback(async () => {
    try {
      const [k, g, v, t] = await Promise.all([
        kundeService.getAll(), grabService.getAll(), vertragService.getAll(), tierService.getAll(),
      ]);
      setItems(k);
      setGraeber(g);
      setVertraege(v);
      setTiere(t);
    } catch { toast.error('Kunden konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNew = () => { setEditing(null); setShowForm(true); };
  const handleSaved = () => { setShowForm(false); setEditing(null); load(); };

  // Verträge je Kunde vorzählen (aktiv vs. nur beendet) – für die Vertragsspalte.
  const vertraegeProKunde = useMemo(() => {
    const map = new Map<number, { aktiv: number; beendet: number }>();
    vertraege.forEach(v => {
      const e = map.get(v.kundeId) || { aktiv: 0, beendet: 0 };
      if (v.status === 'BEENDET') e.beendet++; else e.aktiv++;
      map.set(v.kundeId, e);
    });
    return map;
  }, [vertraege]);

  // Auf einem Tierfriedhof kennt man Kunden über ihre Tiere: je Kunde die Tiere (Besitzer über den
  // Vertrag, sonst über das Grab – so liefert es die API) und die Gräber, in denen sie liegen.
  const grabVon = useMemo(() => new Map(graeber.map(g => [g.id, g])), [graeber]);
  const tiereProKunde = useMemo(() => {
    const map = new Map<number, Tier[]>();
    tiere.forEach(t => { if (t.kundeId != null) map.set(t.kundeId, [...(map.get(t.kundeId) ?? []), t]); });
    return map;
  }, [tiere]);
  const graeberProKunde = useMemo(() => {
    const map = new Map<number, Grab[]>();
    const dazu = (kundeId: number, g?: Grab) => {
      if (!g) return;
      const liste = map.get(kundeId) ?? [];
      if (!liste.some(x => x.id === g.id)) map.set(kundeId, [...liste, g]);
    };
    graeber.forEach(g => { if (g.kundeId != null) dazu(g.kundeId, g); });
    tiere.forEach(t => { if (t.kundeId != null) dazu(t.kundeId, grabVon.get(t.grabId)); });
    return map;
  }, [graeber, tiere, grabVon]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(k => {
      const tiernamen = (tiereProKunde.get(k.id) ?? []).map(t => t.name ?? '').join(' ');
      if (q && !`${k.vorname} ${k.nachname} ${k.kundennummer} ${tiernamen}`.toLowerCase().includes(q)) return false;
      const v = vertraegeProKunde.get(k.id);
      if (filterVertrag === 'MIT' && !v) return false;
      if (filterVertrag === 'AKTIV' && !v?.aktiv) return false;
      if (filterVertrag === 'OHNE' && v) return false;
      return true;
    });
  }, [items, query, filterVertrag, vertraegeProKunde, tiereProKunde]);

  const resetFilters = () => { setQuery(''); setFilterVertrag('ALL'); };
  const hasActiveFilter = query.trim() !== '' || filterVertrag !== 'ALL';

  // Kein Blättern: die Liste läuft durch und lädt beim Scrollen still nach.
  const { sichtbar, Ende } = useStueckweise(filtered);

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Kunden"
        subtitle={`${items.length} ${items.length === 1 ? 'Kunde' : 'Kunden'} im System`}
        actions={<Button onClick={handleNew} icon={<Icon.Plus className="h-4 w-4" />}>Kunde anlegen</Button>}
      />

      {/* Filter & Suche */}
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <div className="relative min-w-[200px] flex-1">
          <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${query.trim() ? 'text-brand-600' : 'text-gray-400'}`}><Icon.Search className="h-4 w-4" /></span>
          <input
            placeholder="Name, Kundennummer oder Tier…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={`${inputClass} pl-9 ${query.trim() ? filterAktiv : ''}`}
          />
        </div>
        <div className="w-48">
          <Select value={filterVertrag} onChange={e => setFilterVertrag(e.target.value as VertragFilter)} aria-label="Vertrag"
            className={filterVertrag !== 'ALL' ? filterAktiv : ''}>
            <option value="ALL">Alle Verträge</option>
            <option value="MIT">Mit Vertrag</option>
            <option value="AKTIV">Nur aktiver Vertrag</option>
            <option value="OHNE">Ohne Vertrag</option>
          </Select>
        </div>
        <FilterStand treffer={filtered.length} gesamt={items.length} einheit="Kunden" aktiv={hasActiveFilter} onZuruecksetzen={resetFilters} />
      </Card>

      {showForm && (
        <KundeFormModal kunde={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={handleSaved} />
      )}

      <div className={tableCard}>
        <div className={tableScroll}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className={`${th} !px-5`}>Kunde</th>
                <th className={`${th} !px-5`}>Tiere und Gräber</th>
                <th className={`${th} !px-5 hidden lg:table-cell`}>Ort</th>
                <th className={`${th} !px-5 hidden lg:table-cell`}>Kontakt</th>
                <th className={`${th} !px-5`}>Vertrag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sichtbar.map(item => {
                const a = ankunft(`kunde-${item.id}`);
                const eigeneTiere = tiereProKunde.get(item.id) ?? [];
                const eigeneGraeber = graeberProKunde.get(item.id) ?? [];
                const v = vertraegeProKunde.get(item.id);
                const telefon = item.mobil || item.telefon;
                return (
                  <tr
                    key={item.id}
                    ref={a.ref}
                    className={`${tableRow} cursor-pointer ${a.className}`}
                    onClick={() => navigate(`/kunden/${item.id}`)}
                  >
                    <td className={`${td} !px-5`}>
                      <span className="block font-medium text-gray-900">{`${item.vorname ?? ''} ${item.nachname}`.trim()}</span>
                      <span className="block text-xs tabular-nums text-gray-400">{item.kundennummer}</span>
                    </td>
                    <td className={`${td} !px-5`}>
                      {eigeneTiere.length > 0 && (
                        <span className="block text-gray-900">{eigeneTiere.map(t => t.name || 'Ohne Namen').join(', ')}</span>
                      )}
                      {eigeneGraeber.length > 0 && (
                        <span className="block text-xs text-gray-500">
                          Abteilung {eigeneGraeber[0].abteilung}, Grab {eigeneGraeber[0].grabnummer}
                          {eigeneGraeber.length > 1 && ` und ${eigeneGraeber.length - 1} weitere`}
                        </span>
                      )}
                    </td>
                    <td className={`${td} !px-5 text-gray-600 hidden lg:table-cell`}>{item.ort}</td>
                    <td className={`${td} !px-5 hidden lg:table-cell`}>
                      {/* Direkt anrufen/schreiben – ohne dabei die Zeile (Detailseite) zu öffnen. */}
                      {telefon && (
                        <a href={`tel:${telefon.replace(/\s/g, '')}`} onClick={e => e.stopPropagation()}
                          className="block tabular-nums text-gray-600 hover:text-brand-700">{telefon}</a>
                      )}
                      {item.email && (
                        <a href={`mailto:${item.email}`} onClick={e => e.stopPropagation()}
                          className="block truncate text-xs text-gray-500 hover:text-brand-700">{item.email}</a>
                      )}
                    </td>
                    <td className={`${td} !px-5 whitespace-nowrap`}>
                      {v && (
                        <span className="inline-flex items-center gap-2 text-gray-700">
                          <span className={`h-2 w-2 rounded-full ${v.aktiv > 0 ? 'bg-brand-500' : 'bg-gray-300'}`} />
                          {v.aktiv > 1 ? `${v.aktiv} aktiv` : v.aktiv === 1 ? 'Aktiv' : 'Beendet'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {Ende}
        </div>
        {filtered.length === 0 && (
          hasActiveFilter ? (
            <EmptyState
              title="Keine Treffer für diesen Filter"
              hint="Kein Kunde passt zu Suche und Vertragsfilter."
              action={<Button variant="secondary" onClick={resetFilters}>Filter zurücksetzen</Button>}
            />
          ) : (
            <EmptyState
              title="Noch keine Kunden"
              hint="Hier stehen die Tierhalter, auf die Gräber und Verträge laufen."
              action={<Button onClick={handleNew} icon={<Icon.Plus className="h-4 w-4" />}>Kunde anlegen</Button>}
            />
          )
        )}
      </div>
    </div>
  );
};

export default Kunden;
